import { useQuery, useQueryClient } from '@tanstack/react-query';
import { articlesAPI, ArticleListResponse } from '../../services/api';
import { Article } from '../../types';

/**
 * Query hooks for Articles (server-paginated list + deleted list).
 *
 * Key root MUST be 'articles' — the slug the realtime-invalidation
 * middleware broadcasts for /api/articles — so another user's change
 * auto-refreshes this list. Invalidating the root key refreshes every
 * paginated/filtered variant and the deleted list at once.
 *
 * NOTE: dropdown data (articlesAPI.getAllForDropdown) must NOT be keyed under
 * ['articles'] — only the management list lives here.
 */
export interface ArticlesListParams {
  skip: number;
  limit: number;
  search?: string;
  extra?: Record<string, any>;
}

export const articlesKeys = {
  root: ['articles'] as const,
  list: (params: ArticlesListParams) => ['articles', 'list', params] as const,
  deleted: ['articles', 'deleted'] as const,
};

export function useArticlesList(params: ArticlesListParams, enabled: boolean) {
  return useQuery<ArticleListResponse>({
    queryKey: articlesKeys.list(params),
    queryFn: () =>
      articlesAPI.getPaginated(
        params.skip,
        params.limit,
        params.search,
        params.extra,
      ),
    enabled,
    keepPreviousData: true,
  });
}

export function useDeletedArticles(enabled: boolean) {
  return useQuery<Article[]>({
    queryKey: articlesKeys.deleted,
    queryFn: () => articlesAPI.getDeleted(),
    enabled,
  });
}

export function useInvalidateArticles() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: articlesKeys.root });
}
